import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import EdpiesLogo from '@/components/EdpiesLogo';
import {
  Sparkles, Brain, ChevronRight, BookOpen, Lightbulb,
  Target, FileText, Library, Dumbbell, ClipboardList,
  GraduationCap, Rocket, ArrowRight, LayoutDashboard, 
  TrendingUp, LogOut, User, PenTool, Crown, ScrollText, Files, Wand2,
  Flame, Trophy, ThumbsUp, Zap, Clock, Atom
} from 'lucide-react';
import axios from 'axios';
import useSubscriptionAccess from '@/hooks/useSubscriptionAccess';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WelcomeScreen = ({ progress, updateProgress }) => {
  const navigate = useNavigate();
  const [animationStep, setAnimationStep] = useState(0);
  const [learningPathSummary, setLearningPathSummary] = useState(null);
  const userName = progress?.user?.name?.split(' ')[0] || 'Learner';
  const { access, tier } = useSubscriptionAccess();

  // Days remaining on a free trial (only meaningful when tier === 'free').
  const trialDaysLeft = (() => {
    const sub = progress?.user?.subscription;
    if (!sub?.endDate || tier !== 'free') return null;
    const ms = new Date(sub.endDate).getTime() - Date.now();
    return ms > 0 ? Math.ceil(ms / (1000 * 60 * 60 * 24)) : 0;
  })();

  // Show plan picker CTA for free / none tiers (i.e. anyone without a paid plan).
  const showPlanPickerCta = access && (tier === 'free' || tier === 'none');

  // Get user ID from token
  const getUserId = () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return null;
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.sub || payload.user_id;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    const timer1 = setTimeout(() => setAnimationStep(1), 300);
    const timer2 = setTimeout(() => setAnimationStep(2), 600);
    const timer3 = setTimeout(() => setAnimationStep(3), 900);
    
    // Fetch learning path summary
    const fetchLearningPathSummary = async () => {
      const userId = getUserId();
      if (userId) {
        try {
          const response = await axios.get(`${API}/learning-engine/summary/${userId}`);
          if (response.data.has_data) {
            setLearningPathSummary(response.data);
          }
        } catch (error) {
          console.log('Learning path summary not available');
        }
      }
    };
    fetchLearningPathSummary();
    
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, []);

  const markWelcomeSeen = () => {
    updateProgress({ 
      hasSeenWelcome: true, 
      welcomeSeenAt: new Date().toISOString()
    });
  };

  const handleResourceHub = () => {
    markWelcomeSeen();
    navigate('/resources-hub');
  };

  const handleAdaptiveTest = () => {
    markWelcomeSeen();
    navigate('/ks2-assessment');
  };

  const handleOtherTools = () => {
    markWelcomeSeen();
    navigate('/explore');
  };

  const handleWritingCoach = () => {
    markWelcomeSeen();
    navigate('/writing-coach');
  };

  const handleAIHub = () => {
    markWelcomeSeen();
    navigate('/ai-hub');
  };

  const handlePersonalStatement = () => {
    markWelcomeSeen();
    navigate('/personal-statement-review');
  };

  const handleTutorPapers = () => {
    markWelcomeSeen();
    navigate('/tutor-papers');
  };

  const handleAIPractice = () => {
    markWelcomeSeen();
    navigate('/ai-practice');
  };

  const handleSTEMLibrary = () => {
    markWelcomeSeen();
    navigate('/stem-library');
  };

  // Check if user is A-Level student
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const yearGroup = (user.year_group || user.yearGroup || user.studentProfile?.yearGroup || '').toLowerCase();
  const userType = (user.user_type || user.userType || '').toLowerCase();
  const isALevel = yearGroup.includes('year 12') || yearGroup.includes('year-12') ||
                   yearGroup.includes('year 13') || yearGroup.includes('year-13') ||
                   userType.includes('a-level') || userType.includes('alevel');
  // STEM Library is currently only positioned for Year 9-10 / GCSE students
  // (the flat-rate "GCSE Unlimited" subscription audience).
  // Permissive matching: handles "Year 9", "year-9", "Year 9 (Age 13-14)", etc.
  const isGcseStage = /year[- ]?(9|10|11)/i.test(yearGroup) ||
                      yearGroup.includes('gcse') || yearGroup.includes('ks4') ||
                      userType.includes('gcse') || userType.includes('ks4');

  const mainOptions = [
    {
      id: 'resources',
      title: 'Resources Hub',
      subtitle: 'Quick revision & concepts',
      description: 'Master key topics with bite-sized lessons, worked examples, and practice questions.',
      icon: Lightbulb,
      color: 'from-rose-500 to-pink-600',
      bgColor: 'bg-rose-500/10',
      borderColor: 'border-rose-500/30',
      shadowColor: 'shadow-rose-500/20',
      badge: 'New',
      badgeColor: 'bg-rose-500',
      features: ['5-min concept reads', 'Worked examples', '11+ twist questions'],
      onClick: handleResourceHub,
      testId: 'resources-hub-card'
    },
    {
      id: 'adaptive',
      title: 'Take Adaptive Assessment',
      subtitle: 'Personalised learning path',
      description: 'Complete a quick diagnostic to unlock your custom 16-week study plan.',
      icon: Brain,
      color: 'from-violet-500 to-indigo-600',
      bgColor: 'bg-violet-500/10',
      borderColor: 'border-violet-500/30',
      shadowColor: 'shadow-violet-500/20',
      badge: 'Recommended',
      badgeColor: 'bg-violet-500',
      features: ['20-25 min assessment', 'Instant results', 'Custom study plan'],
      onClick: handleAdaptiveTest,
      testId: 'adaptive-test-card'
    },
    {
      id: 'tools',
      title: 'Other Tools',
      subtitle: 'Practice & explore',
      description: "Browse Ed's English Library, practice papers, custom banks, and more.",
      icon: Library,
      color: 'from-emerald-500 to-teal-600',
      bgColor: 'bg-emerald-500/10',
      borderColor: 'border-emerald-500/30',
      shadowColor: 'shadow-emerald-500/20',
      badge: null,
      features: ["Ed's English Library", 'Practice papers', 'Custom practice'],
      onClick: handleOtherTools,
      testId: 'other-tools-card'
    },
    {
      id: 'ai-practice',
      title: 'My Growth Zone',
      subtitle: 'Turn gaps into strengths',
      description: 'Create personalised practice papers by selecting topics, difficulty, and year group.',
      icon: Wand2,
      color: 'from-purple-500 to-pink-600',
      bgColor: 'bg-purple-500/10',
      borderColor: 'border-purple-500/30',
      shadowColor: 'shadow-purple-500/20',
      badge: 'New',
      badgeColor: 'bg-purple-500',
      features: ['Choose your topics', 'Set difficulty level', 'Save & reuse papers'],
      onClick: handleAIPractice,
      testId: 'ai-practice-card'
    },
    {
      id: 'ai-hub',
      title: 'AI Learning Hub',
      subtitle: 'Reading Lab & Writing Coach',
      description: 'Access AI-powered reading tools and creative writing coach with instant feedback from Ed.',
      icon: Brain,
      color: 'from-violet-500 to-amber-500',
      bgColor: 'bg-violet-500/10',
      borderColor: 'border-violet-500/30',
      shadowColor: 'shadow-violet-500/20',
      badge: 'AI',
      badgeColor: 'bg-gradient-to-r from-violet-500 to-amber-500',
      features: ['AI Reading Lab', 'AI Writing Coach', 'Chat with Ed Tutor'],
      onClick: handleAIHub,
      testId: 'ai-hub-card'
    },
    // STEM Library — Year 9-10 / GCSE only (flat subscription audience)
    ...(isGcseStage ? [{
      id: 'stem-library',
      title: 'STEM Library',
      subtitle: 'Maths · Physics · Chemistry · Biology',
      description: 'Explore STEM topics with AI-generated explanations, worked examples, and instant Q&A.',
      icon: Atom,
      color: 'from-cyan-500 to-blue-600',
      bgColor: 'bg-cyan-500/10',
      borderColor: 'border-cyan-500/30',
      shadowColor: 'shadow-cyan-500/20',
      badge: 'New',
      badgeColor: 'bg-cyan-500',
      features: ['4 STEM subjects', 'AI explanations', 'Practice & quiz mode'],
      onClick: handleSTEMLibrary,
      testId: 'stem-library-card'
    }] : []),
    // Personal Statement Review - A-Level only
    ...(isALevel ? [{
      id: 'personal-statement',
      title: 'Personal Statement Review',
      subtitle: 'AI-powered UCAS statement feedback',
      description: 'Get expert AI feedback on your university personal statement with scores and improvements.',
      icon: ScrollText,
      color: 'from-violet-500 to-purple-600',
      bgColor: 'bg-violet-500/10',
      borderColor: 'border-violet-500/30',
      shadowColor: 'shadow-violet-500/20',
      badge: 'Premium',
      badgeColor: 'bg-violet-500',
      features: ['Detailed scoring rubric', 'Oxbridge & US guidance', 'Improvement suggestions'],
      onClick: handlePersonalStatement,
      testId: 'personal-statement-card'
    }] : [])
  ];

  return (
    <div 
      data-testid="welcome-screen"
      className="min-h-screen bg-gradient-to-br from-slate-950 via-indigo-950 to-slate-950 flex items-center justify-center p-4 overflow-hidden"
    >
      {/* Top Navigation Bar */}
      <div className="fixed top-0 left-0 right-0 z-50 p-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/student-dashboard')}
              className="text-white hover:bg-white/20"
              data-testid="dashboard-btn"
            >
              <LayoutDashboard className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">Dashboard</span>
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                // Clear all auth data from localStorage FIRST
                localStorage.clear(); // Nuclear option - clear everything
                // Reset React state
                if (updateProgress) updateProgress(null);
                // Force full page reload to reset all state
                window.location.replace('/');
              }}
              className="text-white hover:bg-white/20"
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Animated background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-violet-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-purple-500/5 rounded-full blur-3xl" />
        
        {/* Floating particles */}
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-white/20 rounded-full"
            initial={{
              x: Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 1000),
              y: Math.random() * (typeof window !== 'undefined' ? window.innerHeight : 800),
              opacity: 0
            }}
            animate={{
              y: [null, Math.random() * -200],
              opacity: [0, 0.5, 0],
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          />
        ))}
      </div>

      <div className="relative z-10 max-w-4xl w-full">
        {/* Logo and Welcome Header */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: animationStep >= 0 ? 1 : 0, y: animationStep >= 0 ? 0 : -30 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="text-center mb-8"
        >
          <div className="inline-flex items-center justify-center mb-4">
            <div className="relative">
              <EdpiesLogo size="xl" />
              <motion.div
                className="absolute -top-1 -right-1"
                animate={{ rotate: [0, 10, -10, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <Sparkles className="w-5 h-5 text-amber-400" />
              </motion.div>
            </div>
          </div>

          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
            Welcome, <span className="bg-gradient-to-r from-violet-400 to-indigo-400 bg-clip-text text-transparent">{userName}</span>!
          </h1>
          
          <p className="text-base text-gray-400 max-w-lg mx-auto">
            Choose how you'd like to start your learning journey
          </p>
        </motion.div>

        {/* Pick-your-plan CTA — visible to free / none tier users */}
        {showPlanPickerCta && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: animationStep >= 1 ? 1 : 0, y: animationStep >= 1 ? 0 : 20 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
            className="mb-6"
            data-testid="welcome-plan-picker-cta"
          >
            <div className="rounded-2xl border border-purple-500/40 bg-gradient-to-r from-purple-600/20 via-fuchsia-600/15 to-indigo-600/20 backdrop-blur-xl p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-4">
              <div className="flex items-start gap-4 flex-1">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-fuchsia-600 flex items-center justify-center flex-shrink-0">
                  <Crown className="w-6 h-6 text-white" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h3 className="text-lg md:text-xl font-bold text-white">
                      {tier === 'free' ? 'Your Free Trial is active' : 'Choose your plan to begin'}
                    </h3>
                    {tier === 'free' && trialDaysLeft !== null && (
                      <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30">
                        <Clock className="w-3 h-3 mr-1" />
                        {trialDaysLeft} day{trialDaysLeft === 1 ? '' : 's'} left
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-slate-300">
                    {tier === 'free'
                      ? 'Pick a plan now to keep going after your trial ends — Beginner from £7.99/month.'
                      : 'Unlock practice tests, mocks and adaptive learning. Plans tailored to your year group.'}
                  </p>
                </div>
              </div>
              <Button
                onClick={() => navigate('/subscription')}
                className="bg-gradient-to-r from-purple-600 to-fuchsia-600 hover:from-purple-700 hover:to-fuchsia-700 text-white h-11 px-6 font-semibold whitespace-nowrap"
                data-testid="welcome-pick-plan-btn"
              >
                {tier === 'free' ? 'Choose Your Plan' : 'See Plans'}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </motion.div>
        )}

        {/* Main 3 Options Grid */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: animationStep >= 1 ? 1 : 0, y: animationStep >= 1 ? 0 : 30 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6"
        >
          {mainOptions.map((option, index) => {
            const IconComponent = option.icon;
            
            return (
              <motion.div
                key={option.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: animationStep >= 2 ? 1 : 0, y: animationStep >= 2 ? 0 : 20 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <Card 
                  className={`relative bg-white/5 backdrop-blur-xl border-white/10 hover:${option.borderColor} cursor-pointer transition-all duration-300 hover:scale-[1.02] hover:shadow-xl ${option.shadowColor} group h-full`}
                  onClick={option.onClick}
                  data-testid={option.testId}
                >
                  {/* Gradient top border on hover */}
                  <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${option.color} opacity-0 group-hover:opacity-100 transition-opacity rounded-t-lg`} />
                  
                  <CardContent className="p-5 flex flex-col h-full">
                    {/* Header with icon and badge */}
                    <div className="flex items-start justify-between mb-3">
                      <div className={`p-3 rounded-xl ${option.bgColor}`}>
                        <IconComponent className={`w-6 h-6 bg-gradient-to-r ${option.color} bg-clip-text`} style={{ color: option.id === 'resources' ? '#f43f5e' : option.id === 'adaptive' ? '#8b5cf6' : '#10b981' }} />
                      </div>
                      {option.badge && (
                        <Badge className={`${option.badgeColor} text-white text-xs px-2 py-0.5`}>
                          {option.badge}
                        </Badge>
                      )}
                    </div>

                    {/* Title and subtitle */}
                    <h3 className="text-lg font-bold text-white mb-1">{option.title}</h3>
                    <p className="text-sm text-gray-400 mb-3">{option.subtitle}</p>

                    {/* Description */}
                    <p className="text-sm text-gray-500 mb-4 flex-grow">
                      {option.description}
                    </p>

                    {/* Features list */}
                    <div className="space-y-1.5 mb-4">
                      {option.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-xs text-gray-400">
                          <div className={`w-1 h-1 rounded-full bg-gradient-to-r ${option.color}`} />
                          {feature}
                        </div>
                      ))}
                    </div>

                    {/* CTA */}
                    <div className={`flex items-center justify-between pt-3 border-t border-white/10`}>
                      <span className="text-sm font-medium text-white group-hover:text-white/90">
                        Get Started
                      </span>
                      <ChevronRight className="w-4 h-4 text-gray-500 group-hover:text-white group-hover:translate-x-1 transition-all" />
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Learning Path Summary Widget - Only shows if user has data */}
        {learningPathSummary && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: animationStep >= 2 ? 1 : 0, y: animationStep >= 2 ? 0 : 20 }}
            transition={{ duration: 0.5, delay: 0.35 }}
            className="mb-6"
          >
            <Card 
              className="bg-gradient-to-r from-purple-900/40 to-indigo-900/40 border-2 border-purple-500/30 hover:border-purple-400/50 cursor-pointer transition-all duration-300 hover:scale-[1.01] group overflow-hidden"
              onClick={() => navigate('/my-learning-path')}
              data-testid="learning-path-summary-card"
            >
              <CardContent className="p-5">
                <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
                  {/* Icon */}
                  <div className="p-3 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 shadow-lg shrink-0">
                    <TrendingUp className="h-6 w-6 text-white" />
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-lg font-bold text-white">Your Learning Path</h3>
                      <Badge className="bg-purple-500/30 text-purple-300 text-xs border-purple-500/30">
                        <Sparkles className="w-3 h-3 mr-1" />
                        Personalised
                      </Badge>
                    </div>
                    
                    {/* Stats Row */}
                    <div className="flex flex-wrap gap-4 mb-3">
                      {/* Overall Strength */}
                      <div className="flex items-center gap-2">
                        <Zap className="w-4 h-4 text-purple-400" />
                        <span className="text-sm text-gray-300">
                          <span className="font-bold text-white">{learningPathSummary.overall_strength}%</span> strength
                        </span>
                      </div>
                      
                      {/* Mastered */}
                      <div className="flex items-center gap-2">
                        <Trophy className="w-4 h-4 text-emerald-400" />
                        <span className="text-sm text-gray-300">
                          <span className="font-bold text-white">{learningPathSummary.topics_summary?.mastery || 0}</span> mastered
                        </span>
                      </div>
                      
                      {/* Focus Areas */}
                      <div className="flex items-center gap-2">
                        <Flame className="w-4 h-4 text-amber-400" />
                        <span className="text-sm text-gray-300">
                          <span className="font-bold text-white">{learningPathSummary.topics_summary?.growth || 0}</span> focus areas
                        </span>
                      </div>
                    </div>
                    
                    {/* Focus Now Preview */}
                    {learningPathSummary.focus_now?.length > 0 && (
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-amber-400 font-medium">Focus Now:</span>
                        <span className="text-gray-400">
                          {learningPathSummary.focus_now.slice(0, 2).map(t => t.topic_name).join(', ')}
                          {learningPathSummary.focus_now.length > 2 && ` +${learningPathSummary.focus_now.length - 2} more`}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {/* CTA */}
                  <Button 
                    className="bg-gradient-to-r from-purple-500 to-indigo-600 hover:opacity-90 whitespace-nowrap shrink-0"
                  >
                    View Plan
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* A-Level Hub Banner - Only for A-Level students */}
        {isALevel && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: animationStep >= 2 ? 1 : 0, y: animationStep >= 2 ? 0 : 20 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="mb-6"
          >
            <Card 
              className="bg-gradient-to-r from-orange-500/20 to-red-500/20 border-2 border-orange-500/30 hover:border-orange-400/50 cursor-pointer transition-all duration-300 hover:scale-[1.01] group overflow-hidden"
              onClick={() => navigate('/subjects/a-levels')}
              data-testid="a-level-hub-banner"
            >
              <CardContent className="p-5">
                <div className="flex flex-col md:flex-row items-center gap-4">
                  <div className="p-4 rounded-2xl bg-gradient-to-br from-orange-500 to-red-600 shadow-lg">
                    <GraduationCap className="h-8 w-8 text-white" />
                  </div>
                  <div className="flex-1 text-center md:text-left">
                    <div className="flex items-center gap-2 justify-center md:justify-start mb-1">
                      <h3 className="text-xl font-bold text-white">Eds A-Level Hub™</h3>
                      <Badge className="bg-orange-500 text-white text-xs">A-Level</Badge>
                    </div>
                    <p className="text-orange-300 text-sm">
                      Study • Practice • UniGuide - Your complete A-Level companion
                    </p>
                    <div className="flex flex-wrap gap-2 mt-2 justify-center md:justify-start">
                      <span className="px-2 py-0.5 bg-orange-500/20 text-orange-300 rounded-full text-xs">Maths</span>
                      <span className="px-2 py-0.5 bg-red-500/20 text-red-300 rounded-full text-xs">Physics</span>
                      <span className="px-2 py-0.5 bg-amber-500/20 text-amber-300 rounded-full text-xs">Chemistry</span>
                      <span className="px-2 py-0.5 bg-pink-500/20 text-pink-300 rounded-full text-xs">Biology</span>
                      <span className="px-2 py-0.5 bg-cyan-500/20 text-cyan-300 rounded-full text-xs">+4 more</span>
                    </div>
                  </div>
                  <Button className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 whitespace-nowrap">
                    <Rocket className="h-4 w-4 mr-2" />
                    Open Hub
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Quick tip */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: animationStep >= 3 ? 1 : 0 }}
          transition={{ duration: 0.4, delay: 0.3 }}
          className="text-center"
        >
          <p className="text-sm text-gray-500">
            <span className="text-violet-400">Tip:</span> Taking the adaptive test first helps us personalise your experience
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default WelcomeScreen;
